<template>
  <div class="d2c-your-detail__container">
    <BaseButton
      text="Back to 'Your quote'"
      flat
      ripple
      class="font-weight-semi-bold d-flex back-btn back-previous__btn-link pa-0 mb-sm-5"
      icon="mdi-chevron-left"
      color="#106175"
      small
      @click="backToYourQuote"
    />
    <!-- Stepper Title -->
    <v-row class="mb-7">
      <v-col class="d-flex justify-space-between section-title">
        <div>
          <h1 class="font-28 font-weight-bold black2-color">
            Your application
          </h1>
        </div>
        <BaseButton
          text="Save &amp; exit"
          flat
          depressed
          ripple
          class="font-weight-semi-bold d2c-save-exit mt-2 mr-2 py-3 d-flex"
          icon="mdi-content-save"
          color="#106175"
          small
          @click="saveAndExit"
        />
      </v-col>
    </v-row>
    <ValidationObserver ref="observerFormApplication">
      <v-form ref="submitForm">
        <v-row>
          <v-col class="d-flex justify-space-between flex-wrap section-title">
            <div>
              <h2 class="font-20 font-weight-bold black2-color">
                Policy details
              </h2>
            </div>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" xl="6" class="py-0">
            <BaseLabel
              text="When would you like your policy to start?"
              label-class="font-18 font-weight-medium mb-1"
            />
            <BaseInputDate
              vid="PolicyStartDate"
              :dataUiid="form.policyStartDate.InterfaceKey"
              :value="form.policyStartDate.Value"
              v-model="form.policyStartDate.Value"
              :rules="policyStartDateRules"
              styleClass="d2c-date-input"
            />
          </v-col>
        </v-row>

        <v-row :class="form.documentDeliveryPreference.Value ? 'mb-9' : 'mb-7'">
          <v-col cols="12" xl="8" class="py-0">
            <BaseLabel text="Document delivery preferences" />
            <BaseToggleButton
              v-model="documentDeliveryPreferenceLocal"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
              :toggleButtonText="{
                yes: 'Online',
                no: 'Post',
                yesDataUiid: `${form.documentDeliveryPreference.InterfaceKey}_Online`,
                noDataUiid: `${form.documentDeliveryPreference.InterfaceKey}_Postal`,
              }"
            />
          </v-col>
        </v-row>

        <v-row class="mt-2px">
          <v-col class="d-flex justify-space-between flex-wrap section-title">
            <div>
              <h2 class="font-20 font-weight-bold black2-color">
                Applicant details
              </h2>
            </div>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" xl="6" class="py-0">
            <BaseLabel text="Phone number" />
            <BaseInput
              vid="Applicant1TelNo"
              class="d2c-input"
              v-model="form.applicant1TelNo.Value"
              :data-uiid="form.applicant1TelNo.InterfaceKey"
              :hide-details="false"
              :rules="applicantPhoneNumberRules"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" xl="6" class="py-0">
            <BaseLabel text="Email address" />
            <BaseInput
              vid="Applicant1EmailAddress"
              class="d2c-input"
              v-model="form.applicant1EmailAddress.Value"
              :data-uiid="form.applicant1EmailAddress.InterfaceKey"
              :hide-details="false"
              :rules="applicantEmailAddressRules"
            />
          </v-col>
        </v-row>

        <v-row :class="[{ 'mb-10': !isEnteringCorrespondenceAddressManually }]">
          <v-col cols="12" xl="8" class="py-0">
            <BaseLabel
              v-if="isShowCorrespondenceAddressLabel"
              text="Correspondence address"
              :hintText="ADDRESS_FINDER_INPUT_DESCRIPTION"
            />
            <BaseAlert
              color="var(--blue-100)"
              class="pa-4 mt-4"
              :style="{
                'max-width': '658px',
              }"
            >
              <div class="d-flex flex-no-wrap">
                <div class="mr-3">
                  <v-icon color="#5285c0">mdi-alert-circle</v-icon>
                </div>
                <div>
                  <span class="font-weight-bold">Note!</span> To be eligible for
                  this policy, You (the person named on the policy certificate)
                  must permanently reside in the United Kingdom. Or, if insured
                  in the name of a limited company, it must be registered with
                  Companies House in the United Kingdom.
                </div>
              </div>
            </BaseAlert>
            <BaseAddressLookup
              dataUiid="CorrespondenceAddress_Find_Property"
              showManualAddressForm
              manualAddressFormText="Correspondence address"
              :placeholder="
                $vuetify.breakpoint.lgAndUp
                  ? 'Start typing your address or postcode...'
                  : 'Start typing...'
              "
              manualFormUiid="CorrespondenceAddress"
              addressSummaryWrapper="mt-1 mb-0 address-summary__wrapper"
              id="address-lookup-correspondence"
              :address="correspondenceAddress"
              @address-selected="
                (response) => addressSelected(response, correspondenceAddress)
              "
              @is-address-selected="correspondenceAddressLabel"
              @use-address-finder="
                isEnteringCorrespondenceAddressManually = false
              "
              @enter-address-manually="
                isEnteringCorrespondenceAddressManually = true
              "
              @is-complete-fill-address="
                (val) => {
                  isEnteringCorrespondenceAddressManually = !val;
                }
              "
              :hideDetails="false"
              :rules="{
                required: {
                  fieldLabel: 'Correspondence address',
                },
              }"
              :style="{
                'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
              }"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            />
          </v-col>
        </v-row>

        <v-row v-if="showSecondApplicantDetails">
          <v-col
            class="d-flex justify-space-between flex-wrap section-title pt-0"
          >
            <div>
              <h2 class="font-20 font-weight-bold black2-color">
                Second Applicant details
              </h2>
            </div>
          </v-col>
        </v-row>

        <v-row v-if="showSecondApplicantDetails">
          <v-col cols="12" xl="6" class="py-0">
            <BaseLabel text="Phone number" />
            <BaseInput
              vid="Applicant2TelNo"
              class="d2c-input"
              v-model="form.applicant2TelNo.Value"
              :data-uiid="form.applicant2TelNo.InterfaceKey"
              :hide-details="false"
              :rules="applicantPhoneNumberRules"
            />
          </v-col>
        </v-row>

        <v-row v-if="showSecondApplicantDetails">
          <v-col cols="12" xl="6" class="py-0">
            <BaseLabel text="Email address" />
            <BaseInput
              vid="Applicant2EmailAddress"
              class="d2c-input mb-3"
              v-model="form.applicant2EmailAddress.Value"
              :data-uiid="form.applicant2EmailAddress.InterfaceKey"
              :hide-details="false"
              :rules="applicantEmailAddressRules"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col
            class="d-flex justify-space-between flex-wrap section-title pt-0"
          >
            <div>
              <h2 class="font-20 font-weight-bold black2-color">
                Mortgage details (optional)
              </h2>
            </div>
          </v-col>
        </v-row>

        <v-row :class="form.useMortgageInformation.Value ? 'mb-9' : 'mb-7'">
          <v-col cols="12" xl="8" class="py-0">
            <BaseLabel
              text="Do you want to specify information about your mortgage?"
              :helpText="LABEL_CONSTANTS.USE_MORTGAGE_INFORMATION_HELP_TEXT"
            />
            <BaseToggleButton
              v-model="form.useMortgageInformation.Value"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
              :toggleButtonText="{
                yesDataUiid: `${form.useMortgageInformation.InterfaceKey}_Yes`,
                noDataUiid: `${form.useMortgageInformation.InterfaceKey}_No`,
              }"
            />
          </v-col>
        </v-row>

        <template v-if="form.useMortgageInformation.Value">
          <v-row class="mt-2px">
            <v-col cols="12" xl="6" class="py-0">
              <BaseLabel text="Mortgage lender" />
              <BaseInput
                vid="MortgageLender"
                class="d2c-input"
                v-model="form.mortgageLender.Value"
                :data-uiid="form.mortgageLender.InterfaceKey"
                :hide-details="false"
                :rules="{
                  max: {
                    length: 30,
                    errorMessageTemplate: ERROR_MSG_MAX_CHARACTER,
                    fieldLabel: 'Mortgage lender',
                  },
                }"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" xl="6" class="py-0">
              <BaseLabel
                text="Mortgage completion date"
                label-class="font-18 font-weight-medium mb-1"
              />
              <BaseInputDate
                vid="MortgageStartDate"
                :dataUiid="form.mortgageStartDate.InterfaceKey"
                :value="form.mortgageStartDate.Value"
                v-model="form.mortgageStartDate.Value"
                styleClass="d2c-date-input"
                :rules="mortgageStartDateRules"
              />
            </v-col>
          </v-row>
        </template>

        <v-row class="mt-2px">
          <v-col class="d-flex justify-space-between flex-wrap section-title">
            <div>
              <h2 class="font-20 font-weight-bold black2-color">
                Solicitor details (optional)
              </h2>
            </div>
          </v-col>
        </v-row>

        <v-row :class="form.useSolicitorInformation.Value ? 'mb-9' : 'mb-12'">
          <v-col cols="12" xl="8" class="py-0">
            <BaseLabel
              text="Do you want to specify information about your solicitor?"
              :helpText="LABEL_CONSTANTS.USE_SOLICITOR_INFORMATION_HELP_TEXT"
            />
            <BaseToggleButton
              v-model="form.useSolicitorInformation.Value"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
              :toggleButtonText="{
                yesDataUiid: `${form.useSolicitorInformation.InterfaceKey}_Yes`,
                noDataUiid: `${form.useSolicitorInformation.InterfaceKey}_No`,
              }"
            />
          </v-col>
        </v-row>

        <template v-if="form.useSolicitorInformation.Value">
          <v-row>
            <v-col cols="12" xl="6" class="py-0">
              <BaseLabel text="Solicitor name" />
              <BaseInput
                vid="SolicitorName"
                class="d2c-input mb-2"
                v-model="form.solicitorName.Value"
                :data-uiid="form.solicitorName.InterfaceKey"
                :hide-details="false"
                :rules="solicitorNameRules"
              />
            </v-col>
          </v-row>

          <v-row :class="[{ 'mb-9': !isEnteringSolicitorAddressManually }]">
            <v-col cols="12" xl="8" class="py-0">
              <BaseLabel
                v-if="isShowSolicitorAddressLabel"
                text="Solicitor address"
              />
              <BaseAddressLookup
                dataUiid="SolicitorAddress_Find_Property"
                showManualAddressForm
                manualAddressFormText="Solicitor address"
                :placeholder="
                  $vuetify.breakpoint.lgAndUp
                    ? 'Start typing your address or postcode...'
                    : 'Start typing...'
                "
                manualFormUiid="SolicitorAddress"
                addressSummaryWrapper="mt-1 mb-0 address-summary__wrapper"
                id="address-lookup-solicitor"
                :address="solicitorAddress"
                @address-selected="
                  (response) => addressSelected(response, solicitorAddress)
                "
                @is-address-selected="solicitorAddressLabel"
                @use-address-finder="isEnteringSolicitorAddressManually = false"
                @enter-address-manually="
                  isEnteringSolicitorAddressManually = true
                "
                @is-complete-fill-address="
                  (val) => {
                    isEnteringSolicitorAddressManually = !val;
                  }
                "
                :hideDetails="false"
                :style="{
                  'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
                }"
                :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              />
            </v-col>
          </v-row>

          <v-row v-if="showSolicitorEmailAddress">
            <v-col cols="12" xl="6" class="py-0">
              <BaseLabel text="Solicitor email address" />
              <BaseInput
                vid="SolicitorEmailAddress"
                class="d2c-input"
                v-model="form.solicitorEmailAddress.Value"
                :data-uiid="form.solicitorEmailAddress.InterfaceKey"
                :hide-details="false"
                :rules="{
                  required: {
                    required: true,
                    fieldLabel: 'Solicitor email address',
                  },
                  max: {
                    length: 50,
                    errorMessageTemplate: ERROR_MSG_MAX_CHARACTER,
                    fieldLabel: 'Email address',
                  },
                  regex: {
                    pattern: EMAIL_REGEX,
                    customMessage: ERROR_INVALID_EMAIL_ADDRESS,
                  },
                }"
              />
            </v-col>
          </v-row>

          <v-row class="mb-9">
            <v-col cols="12" xl="8" class="py-0">
              <BaseLabel
                text="Email intention to solicitor?"
                :helpText="LABEL_CONSTANTS.EMAIL_ACCEPT_TO_SOLICITOR_HELP_TEXT"
                labelClass="mb-2 font-18 font-weight-medium"
              />
              <BaseToggleButton
                :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
                minWidth="110px"
                v-model="form.emailAcceptToSolicitor.Value"
                pslStyle="button"
                :data-uiid="form.emailAcceptToSolicitor.InterfaceKey"
                :toggleButtonText="{
                  yesDataUiid: `${form.emailAcceptToSolicitor.InterfaceKey}_Yes`,
                  noDataUiid: `${form.emailAcceptToSolicitor.InterfaceKey}_No`,
                }"
              />
            </v-col>
          </v-row>

          <v-row class="mb-9">
            <v-col cols="12" xl="8" class="py-0">
              <BaseLabel
                text="Email risk notification to solicitor?"
                :helpText="
                  LABEL_CONSTANTS.EMAIL_NOTIFICATION_TO_SOLICITOR_HELP_TEXT
                "
                labelClass="mb-2 font-18 font-weight-medium"
              />
              <BaseToggleButton
                :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
                minWidth="110px"
                v-model="form.emailNotificationToSolicitor.Value"
                pslStyle="button"
                :data-uiid="form.emailNotificationToSolicitor.InterfaceKey"
                :toggleButtonText="{
                  yesDataUiid: `${form.emailNotificationToSolicitor.InterfaceKey}_Yes`,
                  noDataUiid: `${form.emailNotificationToSolicitor.InterfaceKey}_No`,
                }"
              />
            </v-col>
          </v-row>

          <v-row class="mb-2">
            <v-col class="py-0 additional-info">
              <BaseLabel
                text="Additional information (optional)"
                labelClass="mb-3 font-18 font-weight-medium"
              />
              <BaseTextarea
                vid="AdditionalInfo"
                class="font-18 font-weight-medium"
                hideValidationIcon
                v-model="form.additionalInfo.Value"
                no-resize
                :rules="{
                  max: {
                    length: 200,
                    fieldLabel: 'Additional Information (optional)',
                    errorMessageTemplate:
                      '{0} may not be greater than {1} characters.',
                  },
                }"
              />
            </v-col>
          </v-row>
        </template>
      </v-form>
    </ValidationObserver>

    <v-row>
      <v-col class="d-flex">
        <BaseButton
          text="CONTINUE TO PAYMENT"
          rounded
          class="font-weight-bold continue-button"
          height="60"
          :color="'psorange'"
          textColor="black2-color"
          :fontSize="'font-20'"
          @click="onSubmit"
          :min-width="$vuetify.breakpoint.lgAndUp ? '390' : '100%'"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { EventBus } from "@/common/eventbus.js";
import {
  addFieldLabel,
  convertToBool,
  scrollTop,
  tryGoToNextStep,
} from "@/common/utils";
import LayoutAddressMixin from "@/Mixins/LayoutAddressMixin";
import { ValidationObserver } from "vee-validate";
import { mapActions, mapState, mapGetters } from "vuex";

import {
  DOC_DELIVERY_PREF,
  EXIT_DESTINATION,
} from "@/constants/common.constants";
import { ADDRESS_FINDER_INPUT_DESCRIPTION } from "@/constants/description.constants";
import { YOUR_APPLICATION_FORM } from "@/constants/form.constants";
import {
  ERROR_INVALID,
  ERROR_INVALID_EMAIL_ADDRESS,
  ERROR_MSG_MAX_CHARACTER,
  ERROR_NAME_INVALID,
} from "@/constants/message.constants";
import {
  DATE_REGEX,
  EMAIL_REGEX,
  NAME_REGEX,
} from "@/constants/regex-patterns.constants";
import { ROUTE_NAME } from "@/constants/route.constants";
import {
  APPLICATION_MODULE,
  QUESTIONS_MODULE,
} from "@/store/types/module-types";
import { addressSelected } from "@/common/addresses";
import * as LABEL_CONSTANTS from "@/constants/label.constants";

import BaseButton from "@/components/BaseComponents/BaseButton";
import BaseInput from "@/components/BaseComponents/BaseInput.vue";
import BaseInputDate from "@/components/BaseComponents/BaseInputDate";
import BaseLabel from "@/components/BaseComponents/BaseLabel.vue";
import BaseTextarea from "@/components/BaseComponents/BaseTextarea.vue";
import BaseToggleButton from "@/components/BaseComponents/BaseToggleButton.vue";
import BaseAlert from "@/components/BaseComponents/BaseAlert";
import BaseAddressLookup from "@/components/BaseComponents/BaseAddressLookup";

export default {
  name: "D2CLandlordYourApplicationPage",

  mixins: [LayoutAddressMixin],

  components: {
    BaseButton,
    BaseInput,
    BaseInputDate,
    BaseLabel,
    BaseTextarea,
    BaseToggleButton,
    ValidationObserver,
    BaseAlert,
    BaseAddressLookup,
  },

  data() {
    return {
      LABEL_CONSTANTS,
      ADDRESS_FINDER_INPUT_DESCRIPTION,
      DATE_REGEX,
      ERROR_INVALID_EMAIL_ADDRESS,
      ERROR_MSG_MAX_CHARACTER,
      ERROR_NAME_INVALID,
      EMAIL_REGEX,
      NAME_REGEX,
      correspondenceAddress: {},
      isShowCorrespondenceAddressLabel: true,
      isEnteringCorrespondenceAddressManually: false,
      form: YOUR_APPLICATION_FORM,
      loadingCorrespondence: false,
      loadingSolicitor: false,
      showSecondApplicantDetails: false,
      solicitorAddress: {},
      isShowSolicitorAddressLabel: true,
      isEnteringSolicitorAddressManually: false,
      documentDeliveryPreferenceLocal: true,
      vidCorrespondenceAddressForm: {
        postCode: "CorrespondencePostCode",
        street: "CorrespondenceStreet",
        district: "CorrespondenceDistrict",
        town: "CorrespondenceTown",
        county: "CorrespondenceCounty",
      },
      vidSolicitorAddressForm: {
        postCode: "SolicitorAddressPostCode",
        street: "SolicitorAddressStreet",
        district: "SolicitorAddressDistrict",
        town: "SolicitorAddressTown",
        county: "SolicitorAddressCounty",
      },
    };
  },

  created() {
    this.updateCommissionSacrifice(
      this.applyResponse.CommissionSacrifice ??
        this.quoteResponse.CommissionSacrifice
    );
    this.initDataFromStorage();
    this.initAddressForm();
    this.checkToShowHiddenFields();
    scrollTop();
  },

  mounted() {
    this.setCurrentRefObserverForm(this.$refs.observerFormApplication);
    if (!this.restoringLastStep) {
      return;
    }

    // work around running validate before the data has been added to input
    setTimeout(async () => {
      tryGoToNextStep(
        this.$refs.observerFormApplication,
        (form) => form.validate(),
        ROUTE_NAME.LANDLORDS.D2C.PAYMENT
      );
    }, 0);
  },

  computed: {
    ...mapState(APPLICATION_MODULE, {
      applicationQuestionMap: "questionMap",
      applyResponse: "applyResponse",
    }),

    ...mapState(QUESTIONS_MODULE, {
      questionsQuestionMap: "questionMap",
      restoringLastStep: "restoringLastStep",
      quoteResponse: "quoteResponse",
    }),

    ...mapGetters(APPLICATION_MODULE, ["selectedQuote"]),

    policyStartDateRules() {
      return addFieldLabel(
        {
          required: {
            required: true,
          },
          date_valid: {
            errorMessageTemplate: ERROR_INVALID,
          },
          regex: {
            errorMessageTemplate: ERROR_INVALID,
            pattern: DATE_REGEX,
          },
          between_now_and: {
            future: this.selectedQuote?.QuoteExpiryDate,
          },
        },
        "Policy start date"
      );
    },

    applicantPhoneNumberRules() {
      return addFieldLabel(
        {
          required: {},
          phone_number: {},
        },
        "Phone number"
      );
    },

    applicantEmailAddressRules() {
      return addFieldLabel(
        {
          required: {
            required: true,
          },
          max: {
            length: 50,
            errorMessageTemplate: ERROR_MSG_MAX_CHARACTER,
          },
          regex: {
            pattern: EMAIL_REGEX,
            customMessage: ERROR_INVALID_EMAIL_ADDRESS,
          },
        },
        "Email address"
      );
    },

    mortgageStartDateRules() {
      return addFieldLabel(
        {
          date_valid: {
            errorMessageTemplate: ERROR_INVALID,
          },
          regex: {
            errorMessageTemplate: ERROR_INVALID,
            pattern: DATE_REGEX,
          },
          date_in_range: {
            min: 35,
            max: 1,
            units: "years",
            customMessage:
              "Mortgage completion date must be less than 35 years in the past and be no more than 1 year into the future.",
          },
        },
        "Mortgage completion date"
      );
    },

    solicitorNameRules() {
      return addFieldLabel(
        {
          regex: {
            pattern: NAME_REGEX,
            customMessage: ERROR_NAME_INVALID,
          },
          max: {
            length: 30,
            errorMessageTemplate: ERROR_MSG_MAX_CHARACTER,
          },
        },
        "Solicitor name"
      );
    },

    showSolicitorEmailAddress() {
      return (
        this.form.emailAcceptToSolicitor.Value ||
        this.form.emailNotificationToSolicitor.Value
      );
    },
  },

  watch: {
    "form.useMortgageInformation.Value"(value) {
      if (!value) {
        this.form.mortgageLender.Value = "";
        this.form.mortgageStartDate.Value = "";
      }
    },

    "form.useSolicitorInformation.Value"(value) {
      if (!value) {
        this.form.solicitorName.Value = "";
        this.form.solicitorEmailAddress.Value = "";
        this.form.emailAcceptToSolicitor.Value = false;
        this.form.emailNotificationToSolicitor.Value = false;
        this.form.additionalInfo.Value = "";
        // clear solicitor address
        for (const key in this.solicitorAddress) {
          this.solicitorAddress[key].Value = "";
        }
      }
    },

    "form.emailAcceptToSolicitor.Value"() {
      this.checkToClearSolicitorEmailAddress();
    },

    "form.emailNotificationToSolicitor.Value"() {
      this.checkToClearSolicitorEmailAddress();
    },

    documentDeliveryPreferenceLocal(val) {
      if (val) {
        this.form.documentDeliveryPreference.Value = DOC_DELIVERY_PREF.Online;
      } else {
        this.form.documentDeliveryPreference.Value = DOC_DELIVERY_PREF.Postal;
      }
    },
  },

  methods: {
    ...mapActions(QUESTIONS_MODULE, [
      "toggleGlobalSaveAndExitDialog",
      "updateCommissionSacrifice",
      "setCurrentRefObserverForm",
    ]),
    ...mapActions(APPLICATION_MODULE, [
      "updateApplication",
      "handleD2CSaveAndExit",
    ]),
    addressSelected,
    initDataFromStorage() {
      this.form.policyStartDate = this.applicationQuestionMap.PolicyStartDate;

      this.form.documentDeliveryPreference =
        this.applicationQuestionMap.DocumentDeliveryPreference;
      this.setInitialDocDeliveryPref();

      this.form.applicant1TelNo = this.applicationQuestionMap.Applicant1TelNo;

      this.form.applicant1EmailAddress =
        this.applicationQuestionMap.Applicant1EmailAddress;

      const secondApplicantIncluded =
        this.questionsQuestionMap.SecondApplicantIncluded;
      if (
        secondApplicantIncluded &&
        convertToBool(secondApplicantIncluded.Value)
      ) {
        this.showSecondApplicantDetails = true;
      }

      this.form.applicant2TelNo = this.applicationQuestionMap.Applicant2TelNo;
      this.form.applicant2EmailAddress =
        this.applicationQuestionMap.Applicant2EmailAddress;

      if (!this.showSecondApplicantDetails) {
        this.form.applicant2TelNo.Value = null;
        this.form.applicant2EmailAddress.Value = null;
      }

      this.form.useMortgageInformation =
        this.applicationQuestionMap.UseMortgageInformation;
      if (this.form.useMortgageInformation) {
        this.form.useMortgageInformation.Value = convertToBool(
          this.form.useMortgageInformation.Value
        );
      }

      this.form.mortgageLender = this.applicationQuestionMap.MortgageLender;

      this.form.mortgageStartDate =
        this.applicationQuestionMap.MortgageStartDate;

      this.form.useSolicitorInformation =
        this.applicationQuestionMap.UseSolicitorInformation;
      if (this.form.useSolicitorInformation) {
        this.form.useSolicitorInformation.Value = convertToBool(
          this.form.useSolicitorInformation.Value
        );
        if (!this.form.useSolicitorInformation) {
          this.form.emailAcceptToSolicitor = false;
          this.form.emailNotificationToSolicitor = false;
        }
      }

      this.form.solicitorName = this.applicationQuestionMap.SolicitorName;

      this.form.solicitorEmailAddress =
        this.applicationQuestionMap.SolicitorEmailAddress;

      this.form.emailAcceptToSolicitor =
        this.applicationQuestionMap.EmailAcceptToSolicitor;
      if (this.form.emailAcceptToSolicitor) {
        this.form.emailAcceptToSolicitor.Value = convertToBool(
          this.form.emailAcceptToSolicitor.Value
        );
      }

      this.form.emailNotificationToSolicitor =
        this.applicationQuestionMap.EmailNotificationToSolicitor;
      if (this.form.emailNotificationToSolicitor) {
        this.form.emailNotificationToSolicitor.Value = convertToBool(
          this.form.emailNotificationToSolicitor.Value
        );
      }

      this.form.additionalInfo = this.applicationQuestionMap.AdditionalInfo;
    },

    initAddressForm() {
      this.correspondenceAddress = {
        PostCode: this.applicationQuestionMap.CorrespondencePostCode,
        Street: this.applicationQuestionMap.CorrespondenceStreet,
        District: this.applicationQuestionMap.CorrespondenceDistrict,
        Town: this.applicationQuestionMap.CorrespondenceTown,
        County: this.applicationQuestionMap.CorrespondenceCounty,
      };

      this.solicitorAddress = {
        PostCode: this.applicationQuestionMap.SolicitorAddressPostCode,
        Street: this.applicationQuestionMap.SolicitorAddressStreet,
        District: this.applicationQuestionMap.SolicitorAddressDistrict,
        Town: this.applicationQuestionMap.SolicitorAddressTown,
        County: this.applicationQuestionMap.SolicitorAddressCounty,
      };
    },
    correspondenceAddressLabel(val) {
      this.isShowCorrespondenceAddressLabel = !val;
    },
    solicitorAddressLabel(val) {
      this.isShowSolicitorAddressLabel = !val;
    },
    checkToShowHiddenFields() {
      if (this.form.mortgageLender.Value || this.form.mortgageStartDate.Value) {
        this.form.useMortgageInformation.Value = true;
      }

      if (
        this.form.solicitorName.Value ||
        this.form.solicitorEmailAddress.Value ||
        this.addressFormHasData(this.solicitorAddress)
      ) {
        this.form.useSolicitorInformation.Value = true;
      }
    },

    addressFormHasData(addressFormData) {
      return (
        addressFormData.PostCode.Value ||
        addressFormData.Street.Value ||
        addressFormData.County.Value ||
        addressFormData.District.Value ||
        addressFormData.Town.Value
      );
    },

    backToYourQuote() {
      this.$router.push({ name: ROUTE_NAME.LANDLORDS.D2C.QUOTATION_RESULT });
    },

    checkToClearSolicitorEmailAddress() {
      if (
        !this.form.emailAcceptToSolicitor.Value &&
        !this.form.emailNotificationToSolicitor.Value
      ) {
        this.form.solicitorEmailAddress.Value = "";
      }
    },

    async onSubmit() {
      const isValidForm = await this.$refs.observerFormApplication.validate();

      if (!isValidForm) {
        EventBus.$emit("validation-error", "Validation error occurred");
        return;
      }
      sessionStorage.removeItem("isNewQuoteFromExpiredQuote");
      this.updateApplication({ IsPartialApply: true });
      this.$router.push({ name: ROUTE_NAME.LANDLORDS.D2C.PAYMENT });
    },

    saveAndExit() {
      sessionStorage.removeItem("isNewQuoteFromExpiredQuote");
      this.toggleGlobalSaveAndExitDialog(EXIT_DESTINATION.landlordWelcomePage);
    },

    setInitialDocDeliveryPref() {
      if (!this.form.documentDeliveryPreference.Value) {
        this.form.documentDeliveryPreference.Value = DOC_DELIVERY_PREF.Online; // default to Online
      } else if (
        this.form.documentDeliveryPreference.Value === DOC_DELIVERY_PREF.Postal
      ) {
        this.documentDeliveryPreferenceLocal = false; // make the UI match Postal
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/scss/d2c/claim-detail-section.scss";

.d2c-your-detail__container {
  .select__wrapper {
    max-width: 100% !important;
    margin-bottom: 12px;
  }
  .mt-2px {
    margin-top: 2px;
  }
  .radio-document-delivery-preference {
    :deep(.v-input--radio-group__input) {
      max-width: 376px;
      justify-content: space-between;
      button {
        margin-right: 0px !important;
        min-width: calc(50% - 8px);
      }
    }
  }
}

.additional-info {
  max-width: 464px;
}

@media screen and (min-width: $grid-breakpoints-md) {
  .radio-document-delivery-preference {
    :deep(.v-input--radio-group__input) {
      max-width: 300px !important;
    }
  }
}

@media screen and (min-width: $grid-breakpoints-lg) {
  .title-select {
    max-width: 304px;
  }

  :deep(.d2c-input) {
    max-width: 440px;
  }

  :deep(.d2c-date-input) {
    max-width: 440px;
  }

  :deep(.d2c-input-find-property) {
    max-width: 480px;
  }

  :deep(textarea) {
    margin-bottom: 3px;
    margin-right: 3px;
    height: 107px;
  }
}
</style>
